/**
 * @fileoverview Banner displayed when a super admin is impersonating an organization.
 * Shows the impersonated org name and provides a "Back to Admin" button to exit.
 *
 * Layout: `position: relative` (not sticky) so the banner takes flow-space
 * above the sticky `.app-header` and pushes the logo/title down rather than
 * overlapping it. Background uses the Seedling harvest-amber accent
 * (`var(--amb)`) — the only sanctioned warning/notice color in the brand.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Banner shown at the top of the page when impersonating an organization.
 * Returns null when not impersonating.
 *
 * Uses useAuth() to access impersonatedOrg, stopImpersonation, and adminPath.
 * The "Back to Admin" button calls stopImpersonation() then navigates to adminPath.
 *
 * @returns {React.ReactElement|null} The impersonation banner or null
 */
function ImpersonationBanner() {
  const { impersonatedOrg, stopImpersonation, adminPath } = useAuth();
  const navigate = useNavigate();

  if (!impersonatedOrg) {
    return null;
  }

  /**
   * Handle "Back to Admin" click: stop impersonation and navigate to admin panel.
   */
  const handleBackToAdmin = async () => {
    await stopImpersonation();
    navigate(adminPath || '/');
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="impersonation-banner"
      role="status"
      aria-live="polite"
    >
      <span>Viewing as: {impersonatedOrg.name}</span>
      <button
        type="button"
        className="impersonation-banner__btn"
        onClick={handleBackToAdmin}
      >
        Back to Admin
      </button>
    </div>
  );
}

ImpersonationBanner.propTypes = {};

export default ImpersonationBanner;
